import React, { useEffect, useRef, useState } from 'react';
import { HotspotNumber, HotspotPositionMarker } from './HotspotMarker';
import { hotspotMarkerStyle, moveHotspotRectangle, resizeHotspotRectangle } from './hotspotPoints';
import classes from './HotspotImageBlock.module.css';

const CORNERS = { 'top-left': 'supérieur gauche', 'top-right': 'supérieur droit', 'bottom-left': 'inférieur gauche', 'bottom-right': 'inférieur droit' };

export default function HotspotRectangleSurface({ src, point, points, index, onChange, onReady }) {
  const imageRef = useRef(null);
  const moveRef = useRef(null);
  const dragRef = useRef(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const ready = loadedSrc === src;

  useEffect(() => { if (ready) moveRef.current?.focus(); }, [ready]);

  const adjust = (initial, side, delta) => (side === 'move'
    ? moveHotspotRectangle(initial, delta)
    : resizeHotspotRectangle(initial, side, delta));

  const start = (event, side) => {
    if (event.button !== 0 || !ready) return;
    const bounds = imageRef.current.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { side, initial: point, x: event.clientX, y: event.clientY, bounds };
  };
  const move = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    onChange(adjust(drag.initial, drag.side, {
      x: (event.clientX - drag.x) / drag.bounds.width * 100,
      y: (event.clientY - drag.y) / drag.bounds.height * 100,
    }));
  };
  const keyboard = (event, side) => {
    const step = event.shiftKey ? 10 : 1;
    const delta = { ArrowLeft: { x: -step, y: 0 }, ArrowRight: { x: step, y: 0 }, ArrowUp: { x: 0, y: -step }, ArrowDown: { x: 0, y: step } }[event.key];
    if (!delta) return;
    event.preventDefault();
    event.stopPropagation();
    onChange(adjust(point, side, delta));
  };
  const interaction = (side) => ({
    onPointerDown: (event) => start(event, side),
    onKeyDown: (event) => keyboard(event, side),
  });

  return (
    <div className={classes.rectangleSurface} data-hotspot-surface role="group" aria-label={`Placer le repère ${index + 1}`}
      onPointerMove={move}
      onPointerUp={(event) => {
        if (!dragRef.current) return;
        move(event);
        dragRef.current = null;
        if (event.target.hasPointerCapture?.(event.pointerId)) event.target.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => { dragRef.current = null; }}>
      <img ref={imageRef} src={src} alt="Vue complète" draggable={false} className={classes.positionImage}
        onLoad={() => { setLoadedSrc(src); onReady(true); }}
        onError={() => { setLoadedSrc(null); onReady(false); }} />
      {ready && <>
        {points.filter((other) => other.id !== point.id).map((other) => (
          <HotspotPositionMarker key={other.id} point={other} index={points.indexOf(other)} selected={false} />
        ))}
        <div className={`${classes.rectangleEditor} ${classes.rectangle}`} style={hotspotMarkerStyle(point)}>
          <button ref={moveRef} type="button" className={classes.rectangleMove} aria-label="Déplacer l’encadré" {...interaction('move')} />
          {Object.entries(CORNERS).map(([side, label]) => (
            <button key={side} type="button" className={classes.resizeHandle} data-side={side} aria-label={`Redimensionner le coin ${label}`} {...interaction(side)} />
          ))}
          <HotspotNumber point={point} index={index} preview />
        </div>
      </>}
    </div>
  );
}
